import { emitter } from '../../events/bridge-events-emitter';
import { type RPCMessage } from '../../rpc/bridge-api-rpc-message';
import { type TransportEnvelope } from '../bridge-api-transport-envelope';

/**
 * Expected shape of the main process in Electron contexts.
 */
export type DesktopProcess = {
	/**
	 * Sets up an event listener for events which funnel into the main process.
	 * @param channel the identifying channel for this event
	 * @param listener callback invoked when this event is triggered
	 * @returns void
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	on: (channel: string, listener: (event: DesktopEvent, ...args: any[]) => void) => void;

	/**
	 * Clears all listeners on the main process
	 * @param channel a given channel to clean up listeners for
	 * @returns void
	 */
	removeAllListeners: (channel: string) => void;
};

/**
 * Expected shape of events from Electron web view contexts.
 */
export type DesktopEvent = {
	/**
	 * Webview the event originates from.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	sender: any;
};

/**
 * A secure bridge between renderer processes and the main process, allowing the browser transport
 * to communicate with Electron's main process when content is rendered in the webview.
 */
export interface WebviewAPI {
	/**
	 * Check if we're running in an Electron environment
	 */
	isElectron(): boolean;

	/**
	 * Send a message to the main process via IPC
	 * @param envelope The transport envelope to send
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	postMessageToMain(envelope: any): void;

	/**
	 * Listen for messages from the main process
	 * @param callback Function to call when a message is received
	 * @returns Cleanup function to remove the listener
	 */
	onMessageFromMain(callback: (envelope: TransportEnvelope) => void): () => void;
}

/**
 * Global type augmentation for the window object to include the Electron bridge API.
 */
declare global {
	interface Window {
		webviewAPI?: WebviewAPI;
	}
}

/**
 * Utility to check if the current environment is an Electron renderer process.
 *
 * @description Safely checks for the presence of the webviewAPI and verifies
 * it's actually an Electron environment by calling the isElectron method.
 *
 * @returns true if running in Electron renderer, false otherwise
 */
export const isWebviewContext = (): boolean => {
	try {
		return Boolean(
			typeof window !== 'undefined' &&
			window.webviewAPI &&
			typeof window.webviewAPI.isElectron === 'function' &&
			window.webviewAPI.isElectron(),
		);
	} catch {
		return false;
	}
};

/**
 * Utility to safely access the Electron bridge API.
 *
 * @description Provides a type-safe way to access the webviewAPI with proper error handling.
 *
 * @returns The webviewAPI if available, undefined otherwise
 */
export const getWebviewAPI = (): WebviewAPI | undefined => {
	try {
		if (isWebviewContext()) {
			return window.webviewAPI;
		}
		return undefined;
	} catch {
		return undefined;
	}
};

/**
 * Send message to Electron main process via exposed API.
 *
 * @description Centralized function for sending RPC messages from renderer to main process.
 * Handles error cases and emits appropriate transport events.
 *
 * @param message The RPC message to send
 * @param transportSource The source identifier for the transport
 * @returns void
 */
export const sendToMainProcess = (message: RPCMessage, transportSource: string): void => {
	const electronAPI = getWebviewAPI();
	if (!electronAPI) {
		emitter().emit({
			type: 'transport-error',
			transportType: transportSource,
			direction: 'outbound',
			messageId: message.id,
			error: {
				message: 'Webview API not available',
				code: 'WEBVIEW_API_UNAVAILABLE',
			},
		});
		return;
	}

	const envelope: TransportEnvelope = {
		version: 1,
		source: transportSource,
		message,
	};

	emitter().emit({
		type: 'transport-message-sent',
		transportType: transportSource,
		direction: 'outbound',
		messageId: message.id,
		message: { ...envelope },
	});

	// use the exposed to send to main
	electronAPI.postMessageToMain(envelope);
};

/**
 * The IPC channel name used for bridge communication
 */
export const IPC_CHANNEL = 'rovo-bridge-message-channel';

/**
 * An identifier for the `electron` main process.
 */
export const DESKTOP_INSTANCE_ID = 'desktop-main';
